/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { FC, SVGProps } from 'react';

import classNames from 'classnames';

import styles from './abr-space-icon.module.less';

const DEFAULT_COZE_SPACE_ICON_MARKERS = [
  'team_default_icon',
  'user_default_icon',
  'personal.png',
  '/default_icon/',
] as const;

export function isDefaultCozeSpaceIcon(iconUrl?: string): boolean {
  if (!iconUrl) {
    return true;
  }

  return DEFAULT_COZE_SPACE_ICON_MARKERS.some(marker =>
    iconUrl.includes(marker),
  );
}

type AbrSpaceIconGlyphProps = SVGProps<SVGSVGElement> & {
  size?: number;
};

/** 工作空间默认图标：A.BRAIN 品牌几何标 */
export const AbrSpaceIconGlyph: FC<AbrSpaceIconGlyphProps> = ({
  size = 28,
  className,
  ...rest
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 28 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={classNames(styles.glyph, className)}
    aria-hidden
    {...rest}
  >
    <rect
      width="28"
      height="28"
      rx="6"
      fill="var(--abr-brand-primary, #1d2088)"
    />
    <path d="M14 7.5L18.6 16.2H9.4L14 7.5Z" fill="#fff" fillOpacity="0.96" />
    <circle cx="14" cy="18.75" r="2.1" fill="#fff" fillOpacity="0.96" />
    <path
      d="M10.2 20.4H17.8"
      stroke="#fff"
      strokeOpacity="0.72"
      strokeWidth="1.1"
      strokeLinecap="round"
    />
  </svg>
);

export interface AbrSpaceIconProps {
  iconUrl?: string;
  size?: number;
  className?: string;
}

/** ABR 工作空间图标：自定义图标优先，否则使用品牌 SVG */
export const AbrSpaceIcon: FC<AbrSpaceIconProps> = ({
  iconUrl,
  size = 28,
  className,
}) => {
  if (iconUrl && !isDefaultCozeSpaceIcon(iconUrl)) {
    return (
      <img
        src={iconUrl}
        width={size}
        height={size}
        alt=""
        className={classNames(styles.custom, className)}
      />
    );
  }

  return <AbrSpaceIconGlyph size={size} className={className} />;
};
